// -----------------------------------------------------------------------------
// Internal Chat page.
//
// Slack-style two-pane layout:
//   • left  — channel list (team channels + direct messages) with search
//   • right — message thread for the selected conversation with composer
//
// Data is held client-side in `utils/chatStore.ts` (localStorage-backed) so
// the feature is usable end-to-end today. Once `/chat/*` endpoints and the
// `/chatHub` SignalR feed ship (see `docs/pending-endpoints.md → Internal
// Chat`), this page can be switched over to React Query hooks without
// touching the UI.
// -----------------------------------------------------------------------------

import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateTime } from "luxon";

import {
  FaceSmileIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PlusIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

import Modal from "../../layouts/Modal";
import classNames from "../../utils/classNames";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  channelLabel,
  fullNameFor,
  useChatStore,
} from "../../utils/chatStore";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";

const REACTION_EMOJIS = ["👍", "🎉", "❤️", "😂", "🙌", "👀"];

// ---------------------------------------------------------------------------
// Timestamp helpers
// ---------------------------------------------------------------------------

const formatTime = (iso: string): string =>
  DateTime.fromISO(iso).setZone("Europe/London").toFormat("HH:mm");

const formatDayHeading = (iso: string): string => {
  const dt = DateTime.fromISO(iso).setZone("Europe/London");
  const today = DateTime.local().setZone("Europe/London").startOf("day");
  const start = dt.startOf("day");
  const diffDays = Math.round(start.diff(today, "days").days);
  if (diffDays === 0) return "Today";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > -7) return dt.toFormat("cccc"); // e.g. "Tuesday"
  return dt.toFormat("cccc, d LLL"); // e.g. "Tuesday, 15 Apr"
};

const formatRelative = (iso: string | undefined): string => {
  if (!iso) return "";
  const dt = DateTime.fromISO(iso);
  const now = DateTime.local();
  const diffMin = Math.max(0, Math.round(now.diff(dt, "minutes").minutes));
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return dt.toFormat("d LLL");
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Chat() {
  const { setInfo } = usePageNameContext();
  const auth = useAuth();

  const currentUser = useMemo(() => {
    if (!auth.loggedIn) return null;
    const role =
      auth.claims?.isAdmin
        ? "Admin"
        : auth.claims?.isStaff
        ? "Staff"
        : auth.claims?.isMechanic
        ? "Mechanic"
        : auth.claims?.isDriver
        ? "Driver"
        : auth.claims?.isExporter
        ? "Exporter"
        : "Team";
    return {
      id: auth.userId,
      forename: auth.forename,
      surname: auth.surname,
      role,
    };
  }, [
    auth.loggedIn,
    auth.userId,
    auth.forename,
    auth.surname,
    auth.claims,
  ]);

  const chat = useChatStore(currentUser);

  useEffect(() => {
    setInfo({
      name: "Internal chat",
      desc: "Talk to your team in real time — channels, direct messages and shared threads.",
    });
  }, []);

  const usersById = useMemo(() => {
    const map = new Map<string, IChatUser>();
    chat.users.forEach((u) => map.set(u.id, u));
    return map;
  }, [chat.users]);

  // Sort: unread first (team channels), then by most recent message.
  const sortedChannels = useMemo(() => {
    return [...chat.channels].sort((a, b) => {
      const aTime = a.lastMessageAt
        ? Date.parse(a.lastMessageAt)
        : Date.parse(a.createdAt);
      const bTime = b.lastMessageAt
        ? Date.parse(b.lastMessageAt)
        : Date.parse(b.createdAt);
      return bTime - aTime;
    });
  }, [chat.channels]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-select the first channel once the store is ready. We do this
  // lazily (rather than in initial state) so the auto-selection re-runs
  // when the seed is injected on first load of the app.
  useEffect(() => {
    if (selectedId) return;
    const first = sortedChannels.find((c) => c.kind === "channel");
    if (first) setSelectedId(first.id);
  }, [selectedId, sortedChannels]);

  const selected = useMemo(
    () => chat.channels.find((c) => c.id === selectedId) ?? null,
    [chat.channels, selectedId]
  );

  // Mark as read whenever the selected channel changes or new messages
  // arrive for it. Small delay keeps React from thrashing writes during
  // the initial render burst.
  useEffect(() => {
    if (!selected) return;
    const handle = window.setTimeout(() => chat.markRead(selected.id), 150);
    return () => window.clearTimeout(handle);
    // Intentionally depends on both `selectedId` and the list of messages
    // for that channel — we want to re-mark after new activity.
  }, [selected?.id, chat.messagesFor(selected?.id ?? "").length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [search, setSearch] = useState("");
  const filteredChannels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedChannels;
    return sortedChannels.filter((c) => {
      const label = channelLabel(c, chat.me?.id ?? null, usersById).toLowerCase();
      const desc = (c.description ?? "").toLowerCase();
      return label.includes(term) || desc.includes(term);
    });
  }, [sortedChannels, search, chat.me, usersById]);

  const teamChannels = filteredChannels.filter((c) => c.kind === "channel");
  const dmChannels = filteredChannels.filter((c) => c.kind === "dm");

  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);

  return (
    <div className="flex h-[calc(100vh-9rem)] min-h-[34rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <ChannelSidebar
        me={chat.me}
        users={chat.users}
        usersById={usersById}
        teamChannels={teamChannels}
        dmChannels={dmChannels}
        selectedId={selectedId}
        onSelect={setSelectedId}
        search={search}
        onSearch={setSearch}
        unreadFor={chat.unreadCount}
        messagesFor={chat.messagesFor}
        onNewChannel={() => setShowNewChannel(true)}
        onNewDm={() => setShowNewDm(true)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {selected && chat.me ? (
          <ChannelThread
            channel={selected}
            me={chat.me}
            usersById={usersById}
            messages={chat.messagesFor(selected.id)}
            onSend={(body, replyToId) => chat.send(selected.id, body, replyToId)}
            onEdit={chat.editMessage}
            onReact={chat.toggleReaction}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-gray-500">
            Select a conversation from the left to get started.
          </div>
        )}
      </div>

      <NewChannelModal
        open={showNewChannel}
        onClose={() => setShowNewChannel(false)}
        users={chat.users}
        meId={chat.me?.id ?? null}
        onCreate={(input) => {
          const created = chat.createChannel(input);
          setSelectedId(created.id);
          setShowNewChannel(false);
        }}
      />

      <NewDmModal
        open={showNewDm}
        onClose={() => setShowNewDm(false)}
        users={chat.users.filter((u) => !u.isMe)}
        onSelect={(userId) => {
          const channelId = chat.startDm(userId);
          if (channelId) setSelectedId(channelId);
          setShowNewDm(false);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left rail — channels + DMs
// ---------------------------------------------------------------------------

interface ChannelSidebarProps {
  me: IChatUser | null;
  users: IChatUser[];
  usersById: Map<string, IChatUser>;
  teamChannels: IChatChannel[];
  dmChannels: IChatChannel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (value: string) => void;
  unreadFor: (id: string) => number;
  messagesFor: (id: string) => IChatMessage[];
  onNewChannel: () => void;
  onNewDm: () => void;
}

function ChannelSidebar({
  me,
  usersById,
  teamChannels,
  dmChannels,
  selectedId,
  onSelect,
  search,
  onSearch,
  unreadFor,
  messagesFor,
  onNewChannel,
  onNewDm,
}: ChannelSidebarProps) {
  const onlineCount = Array.from(usersById.values()).filter(
    (u) => u.presence === "online"
  ).length;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-gray-50">
      <div className="border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Team chat</p>
            <p className="text-xs text-gray-500">
              {onlineCount} online · {usersById.size} teammates
            </p>
          </div>
          {me ? (
            <ChatAvatar user={me} size="sm" showPresence />
          ) : null}
        </div>
        <label className="mt-3 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search channels and people"
            className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearch("")}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          ) : null}
        </label>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        <SidebarSection
          title="Channels"
          actionLabel="New channel"
          onAction={onNewChannel}
        >
          {teamChannels.length === 0 ? (
            <EmptyHint>No channels match.</EmptyHint>
          ) : (
            teamChannels.map((c) => {
              const unread = unreadFor(c.id);
              const preview = lastMessagePreview(messagesFor(c.id), usersById);
              return (
                <SidebarRow
                  key={c.id}
                  active={selectedId === c.id}
                  onClick={() => onSelect(c.id)}
                  leading={
                    <HashtagIcon className="h-4 w-4 text-gray-500" />
                  }
                  title={c.name}
                  subtitle={preview}
                  unread={unread}
                  trailingText={formatRelative(c.lastMessageAt)}
                />
              );
            })
          )}
        </SidebarSection>

        <SidebarSection
          title="Direct messages"
          actionLabel="New message"
          onAction={onNewDm}
        >
          {dmChannels.length === 0 ? (
            <EmptyHint>No direct messages yet.</EmptyHint>
          ) : (
            dmChannels.map((c) => {
              const otherId = c.memberIds.find((id) => id !== me?.id);
              const other = otherId ? usersById.get(otherId) : undefined;
              const unread = unreadFor(c.id);
              const preview = lastMessagePreview(messagesFor(c.id), usersById);
              return (
                <SidebarRow
                  key={c.id}
                  active={selectedId === c.id}
                  onClick={() => onSelect(c.id)}
                  leading={
                    other ? (
                      <ChatAvatar
                        user={other}
                        size="xs"
                        showPresence
                        tooltip={false}
                      />
                    ) : (
                      <UserPlusIcon className="h-4 w-4 text-gray-500" />
                    )
                  }
                  title={other ? fullNameFor(other) : "Direct message"}
                  subtitle={preview || other?.role}
                  unread={unread}
                  trailingText={formatRelative(c.lastMessageAt)}
                />
              );
            })
          )}
        </SidebarSection>
      </div>
    </aside>
  );
}

function lastMessagePreview(
  messages: IChatMessage[],
  usersById: Map<string, IChatUser>
): string {
  const last = messages[messages.length - 1];
  if (!last) return "";
  const author = usersById.get(last.authorId);
  const prefix = author ? `${author.forename}:` : "";
  const body = last.body.length > 48 ? `${last.body.slice(0, 48)}…` : last.body;
  return [prefix, body].filter(Boolean).join(" ");
}

interface SidebarSectionProps {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}

function SidebarSection({
  title,
  actionLabel,
  onAction,
  children,
}: SidebarSectionProps) {
  return (
    <div className="px-2 py-2">
      <div className="flex items-center justify-between px-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        {onAction ? (
          <button
            type="button"
            onClick={onAction}
            title={actionLabel}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          >
            <PlusIcon className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <li className="px-3 py-2 text-xs text-gray-400">{children}</li>
  );
}

interface SidebarRowProps {
  active: boolean;
  onClick: () => void;
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  unread: number;
  trailingText?: string;
}

function SidebarRow({
  active,
  onClick,
  leading,
  title,
  subtitle,
  unread,
  trailingText,
}: SidebarRowProps) {
  const hasUnread = unread > 0;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          active
            ? "bg-primary-50 text-primary-900"
            : "text-gray-700 hover:bg-white"
        )}
      >
        <span className="flex h-5 w-5 items-center justify-center">
          {leading}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span
              className={classNames(
                "truncate",
                hasUnread && !active ? "font-semibold text-gray-900" : ""
              )}
            >
              {title}
            </span>
            {trailingText ? (
              <span className="shrink-0 text-[10px] text-gray-400">
                {trailingText}
              </span>
            ) : null}
          </span>
          {subtitle ? (
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {subtitle}
            </span>
          ) : null}
        </span>
        {hasUnread ? (
          <span className="shrink-0 rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Thread pane — header, messages, composer
// ---------------------------------------------------------------------------

interface ChannelThreadProps {
  channel: IChatChannel;
  me: IChatUser;
  usersById: Map<string, IChatUser>;
  messages: IChatMessage[];
  onSend: (body: string, replyToId?: string) => void;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
}

function ChannelThread({
  channel,
  me,
  usersById,
  messages,
  onSend,
  onEdit,
  onReact,
}: ChannelThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest message on change. `useLayoutEffect` fires
  // before paint so the view never "jumps" as the user watches.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [channel.id, messages.length]);

  const headerTitle = channelLabel(channel, me.id, usersById);

  const otherMembers = channel.memberIds
    .map((id) => usersById.get(id))
    .filter((u): u is IChatUser => !!u);

  return (
    <>
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {channel.kind === "channel" ? (
              <HashtagIcon className="h-5 w-5 text-gray-400" />
            ) : null}
            <h2 className="truncate text-base font-semibold text-gray-900">
              {channel.kind === "channel" ? channel.name : headerTitle}
            </h2>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              {channel.kind === "channel"
                ? `${channel.memberIds.length} members`
                : "Direct message"}
            </span>
          </div>
          {channel.description ? (
            <p className="truncate text-xs text-gray-500">
              {channel.description}
            </p>
          ) : channel.kind === "dm" ? (
            <p className="truncate text-xs text-gray-500">
              {otherMembers
                .filter((u) => u.id !== me.id)
                .map((u) => u.role || "Team")
                .join(" · ")}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <MemberAvatars members={otherMembers} max={5} />
        </div>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-1 overflow-y-auto bg-gray-50/60 px-4 py-4"
      >
        <MessageList
          messages={messages}
          usersById={usersById}
          meId={me.id}
          onEdit={onEdit}
          onReact={onReact}
        />
      </div>

      <Composer channel={channel} onSend={onSend} />
    </>
  );
}

function MemberAvatars({
  members,
  max = 5,
}: {
  members: IChatUser[];
  max?: number;
}) {
  const shown = members.slice(0, max);
  const remaining = Math.max(0, members.length - shown.length);
  return (
    <div className="flex -space-x-2">
      {shown.map((m) => (
        <ChatAvatar
          key={m.id}
          user={m}
          size="xs"
          className="ring-2 ring-white"
        />
      ))}
      {remaining > 0 ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700 ring-2 ring-white">
          +{remaining}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message list
// ---------------------------------------------------------------------------

interface MessageListProps {
  messages: IChatMessage[];
  usersById: Map<string, IChatUser>;
  meId: string;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
}

function MessageList({
  messages,
  usersById,
  meId,
  onEdit,
  onReact,
}: MessageListProps) {
  if (messages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-400">
        No messages yet — say hello.
      </div>
    );
  }

  // Walk through once and decide where to insert day dividers / author
  // labels so a simple map can render the output.
  type Row =
    | { kind: "day"; key: string; label: string }
    | {
        kind: "msg";
        key: string;
        message: IChatMessage;
        showHeader: boolean;
      };

  const rows: Row[] = [];
  let prev: IChatMessage | null = null;
  for (const m of messages) {
    const day = DateTime.fromISO(m.createdAt).toISODate();
    const prevDay = prev
      ? DateTime.fromISO(prev.createdAt).toISODate()
      : null;
    if (day !== prevDay) {
      rows.push({
        kind: "day",
        key: `day-${day}`,
        label: formatDayHeading(m.createdAt),
      });
    }
    const sameAuthor = prev?.authorId === m.authorId && day === prevDay;
    const within5Min =
      !!prev &&
      Math.abs(Date.parse(m.createdAt) - Date.parse(prev.createdAt)) <
        5 * 60 * 1000;
    rows.push({
      kind: "msg",
      key: m.id,
      message: m,
      showHeader: !sameAuthor || !within5Min,
    });
    prev = m;
  }

  return (
    <>
      {rows.map((row) => {
        if (row.kind === "day") {
          return (
            <div
              key={row.key}
              className="sticky top-0 z-10 my-3 flex items-center justify-center"
            >
              <span className="rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[11px] font-medium text-gray-500 shadow-sm">
                {row.label}
              </span>
            </div>
          );
        }
        const author = usersById.get(row.message.authorId);
        return (
          <MessageRow
            key={row.key}
            message={row.message}
            author={author}
            isMine={row.message.authorId === meId}
            usersById={usersById}
            showHeader={row.showHeader}
            onEdit={onEdit}
            onReact={onReact}
          />
        );
      })}
    </>
  );
}

interface MessageRowProps {
  message: IChatMessage;
  author?: IChatUser;
  isMine: boolean;
  usersById: Map<string, IChatUser>;
  showHeader: boolean;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
}

function MessageRow({
  message,
  author,
  isMine,
  usersById,
  showHeader,
  onEdit,
  onReact,
}: MessageRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    setDraft(message.body);
  }, [message.body, editing]);

  const handleSave = () => {
    if (draft.trim() && draft.trim() !== message.body) {
      onEdit(message.id, draft.trim());
    }
    setEditing(false);
  };

  return (
    <div
      className={classNames(
        "group relative flex gap-3 rounded-md px-2 py-0.5 hover:bg-white",
        showHeader ? "mt-3" : ""
      )}
    >
      <div className="w-9 shrink-0 pt-0.5">
        {showHeader && author ? (
          <ChatAvatar user={author} size="sm" showPresence />
        ) : (
          <span className="block h-4 pt-1 text-center text-[10px] text-gray-400 opacity-0 group-hover:opacity-100">
            {formatTime(message.createdAt)}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {showHeader ? (
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {author ? fullNameFor(author) : "Unknown"}
            </span>
            {author?.role ? (
              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                {author.role}
              </span>
            ) : null}
            <span className="text-[11px] text-gray-400">
              {formatTime(message.createdAt)}
            </span>
          </div>
        ) : null}

        {editing ? (
          <div className="mt-1 rounded-md border border-primary-300 bg-white shadow-sm">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              rows={Math.min(6, Math.max(2, draft.split("\n").length))}
              className="w-full resize-none border-0 bg-transparent px-3 py-2 text-sm focus:ring-0"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditing(false);
                } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
            <div className="flex items-center justify-end gap-2 px-2 py-1.5">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-md bg-primary-600 px-2 py-1 text-xs font-semibold text-white hover:bg-primary-500"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-gray-800">
            {message.body}
            {message.editedAt ? (
              <span className="ml-1 text-[10px] text-gray-400">(edited)</span>
            ) : null}
          </p>
        )}

        {message.reactions.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.reactions.map((r) => {
              const mine = r.userIds.includes(isMine ? author?.id ?? "" : "");
              const names = r.userIds
                .map((id) => {
                  const u = usersById.get(id);
                  return u ? u.forename : "Someone";
                })
                .join(", ");
              return (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => onReact(message.id, r.emoji)}
                  title={`${names} reacted with ${r.emoji}`}
                  className={classNames(
                    "flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                    mine
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  )}
                >
                  <span>{r.emoji}</span>
                  <span className="font-semibold">{r.userIds.length}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="invisible absolute right-2 top-0.5 z-10 flex items-center gap-0.5 rounded-md border border-gray-200 bg-white px-1 py-0.5 text-gray-500 shadow-sm group-hover:visible">
        <button
          type="button"
          onClick={() => setShowEmojiPicker((s) => !s)}
          className="rounded p-1 hover:bg-gray-100 hover:text-gray-700"
          title="Add reaction"
        >
          <FaceSmileIcon className="h-4 w-4" />
        </button>
        {isMine ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded p-1 hover:bg-gray-100 hover:text-gray-700"
            title="Edit message"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
        ) : null}

        {showEmojiPicker ? (
          <div className="absolute right-0 top-8 z-20 flex gap-1 rounded-md border border-gray-200 bg-white p-1.5 shadow-lg">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onReact(message.id, emoji);
                  setShowEmojiPicker(false);
                }}
                className="rounded px-1.5 py-1 text-base hover:bg-gray-100"
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

function Composer({
  channel,
  onSend,
}: {
  channel: IChatChannel;
  onSend: (body: string) => void;
}) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset on channel change so a half-typed draft for one channel doesn't
  // leak into another.
  useEffect(() => {
    setValue("");
  }, [channel.id]);

  // Auto-grow up to ~8 rows.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = value.trim();
    if (!body) return;
    onSend(body);
    setValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholder =
    channel.kind === "channel"
      ? `Message #${channel.name}`
      : `Send a direct message`;

  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-gray-200 bg-white p-3"
    >
      <div className="rounded-lg border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className="w-full resize-none border-0 bg-transparent px-3 py-2.5 text-sm placeholder:text-gray-400 focus:ring-0"
        />
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
          <p className="text-[10px] text-gray-400">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
              Enter
            </kbd>{" "}
            to send,{" "}
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
              Shift+Enter
            </kbd>{" "}
            for a new line
          </p>
          <button
            type="submit"
            disabled={!value.trim()}
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold",
              value.trim()
                ? "bg-primary-600 text-white hover:bg-primary-500"
                : "bg-gray-100 text-gray-400"
            )}
          >
            <PaperAirplaneIcon className="h-3.5 w-3.5" />
            Send
          </button>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Modals — new channel / new DM
// ---------------------------------------------------------------------------

interface NewChannelModalProps {
  open: boolean;
  onClose: () => void;
  users: IChatUser[];
  meId: string | null;
  onCreate: (input: {
    name: string;
    description?: string;
    memberIds: string[];
  }) => void;
}

function NewChannelModal({
  open,
  onClose,
  users,
  meId,
  onCreate,
}: NewChannelModalProps) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setName("");
      setDesc("");
      setMemberIds([]);
    }
  }, [open]);

  const toggleMember = (id: string) =>
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const canCreate = name.trim().length > 0;

  const selectableUsers = users.filter((u) => u.id !== meId);

  return (
    <Modal open={open} setOpen={(v) => !v && onClose()} width="sm:max-w-lg">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Create a channel
          </h3>
          <p className="text-xs text-gray-500">
            Channels are a place for the team to talk about a specific topic
            or workflow.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Name
          </span>
          <div className="mt-1 flex overflow-hidden rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
            <span className="flex items-center bg-gray-50 px-2 text-gray-400">
              <HashtagIcon className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. vehicle-arrivals"
              className="w-full border-0 p-2 text-sm focus:ring-0"
              autoFocus
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Description <span className="font-normal">(optional)</span>
          </span>
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What's this channel for?"
            className="mt-1 w-full rounded-md border-gray-300 p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </label>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Members
          </p>
          <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200">
            {selectableUsers.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-500">
                No teammates to add yet.
              </p>
            ) : (
              selectableUsers.map((u) => {
                const checked = memberIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleMember(u.id)}
                    className={classNames(
                      "flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50",
                      checked ? "bg-primary-50" : ""
                    )}
                  >
                    <ChatAvatar user={u} size="sm" />
                    <span className="flex-1 text-sm">
                      <span className="block font-medium text-gray-900">
                        {fullNameFor(u)}
                      </span>
                      {u.role ? (
                        <span className="block text-xs text-gray-500">
                          {u.role}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={classNames(
                        "flex h-5 w-5 items-center justify-center rounded border",
                        checked
                          ? "border-primary-600 bg-primary-600 text-white"
                          : "border-gray-300 bg-white"
                      )}
                    >
                      {checked ? <CheckIcon className="h-3 w-3" /> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canCreate}
          onClick={() =>
            onCreate({
              name: name.trim(),
              description: desc.trim() || undefined,
              memberIds,
            })
          }
          className={classNames(
            "rounded-md px-3 py-1.5 text-sm font-semibold text-white",
            canCreate
              ? "bg-primary-600 hover:bg-primary-500"
              : "bg-gray-300"
          )}
        >
          Create channel
        </button>
      </div>
    </Modal>
  );
}

interface NewDmModalProps {
  open: boolean;
  onClose: () => void;
  users: IChatUser[];
  onSelect: (userId: string) => void;
}

function NewDmModal({ open, onClose, users, onSelect }: NewDmModalProps) {
  const [term, setTerm] = useState("");
  useEffect(() => {
    if (!open) setTerm("");
  }, [open]);

  const filtered = users.filter((u) => {
    const hay = `${u.forename} ${u.surname} ${u.role ?? ""}`.toLowerCase();
    return hay.includes(term.trim().toLowerCase());
  });

  return (
    <Modal open={open} setOpen={(v) => !v && onClose()} width="sm:max-w-md">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            New direct message
          </h3>
          <p className="text-xs text-gray-500">
            Start a 1-1 conversation with a teammate.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search teammates"
          autoFocus
          className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
        />
      </label>

      <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-gray-200">
        {filtered.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-gray-500">
            <UserGroupIcon className="h-4 w-4 text-gray-400" />
            No teammates match.
          </div>
        ) : (
          filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onSelect(u.id)}
              className="flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50"
            >
              <ChatAvatar user={u} size="sm" showPresence />
              <span className="flex-1 text-sm">
                <span className="block font-medium text-gray-900">
                  {fullNameFor(u)}
                </span>
                {u.role ? (
                  <span className="block text-xs text-gray-500">{u.role}</span>
                ) : null}
              </span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
